import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openPortal } from '../../services/portal';
import { BILLING_REASONS, type AccessReason } from '../../types/agent';
import { Brand, Header } from '../Chat/Header';

/** Shown when the signed-in user may not use the agent (GET /agent-v2/access → enabled=false, or any gated route
 *  answering 403 `agent_v2_access_required`). `reason` picks the copy (sculpt-api PLAN-accounts.md D9, §7;
 *  PLAN-offers.md §6): an unverified address gets a "resend the email" button; a missing subscription (Free full
 *  included), an ended trial or an unpaid renewal get the portal's billing page; the others point at Peerception. Both open in the
 *  browser signed in (services/portal.ts `openPortal`). */
export function NoAccess({
  email,
  reason,
  onCheckAgain,
  onResend,
  onSignOut,
}: {
  email: string | null;
  reason?: AccessReason | null;
  onCheckAgain: () => Promise<boolean>;
  onResend?: () => Promise<{ sent: boolean }>;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);
  const [stillDenied, setStillDenied] = useState(false);
  const [resend, setResend] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle');

  const check = async () => {
    setChecking(true);
    setStillDenied(false);
    try {
      const ok = await onCheckAgain();
      if (!ok) setStillDenied(true);
    } finally {
      setChecking(false);
    }
  };

  const resendMail = async () => {
    if (!onResend || resend === 'busy') return;
    setResend('busy');
    try {
      const { sent } = await onResend();
      setResend(sent ? 'sent' : 'failed');
    } catch {
      setResend('failed');
    }
  };

  const known = !!reason && ['email_unverified', 'organization_suspended', 'no_organization', ...BILLING_REASONS].includes(reason);
  const billing = !!reason && BILLING_REASONS.includes(reason);
  const title = known ? t(`access.reasons.${reason}.title`) : t('access.title');
  const message = known ? t(`access.reasons.${reason}.message`) : t('access.message');
  const denied = known ? t(`access.reasons.${reason}.stillDenied`) : t('access.stillDenied');

  return (
    <div className="flex h-full min-h-0 flex-col bg-pane">
      <Header left={<Brand title={t('app.title')} />} />
      <div className="flex flex-1 flex-col justify-center gap-5 px-4 pb-8">
        <div className="flex flex-col gap-1.5">
          <div className="text-[20px] font-medium leading-[1.2] tracking-[-0.02em] text-ink" data-testid="no-access-title">
            {title}
          </div>
          <div className="text-[13px] leading-[1.45] text-muted">{message}</div>
          {email && (
            <div className="font-mono text-[11px] text-faint">
              {t('access.signedInAs')} <span className="text-accent-dark">{email}</span>
            </div>
          )}
        </div>
        {stillDenied && <div className="rounded-md border border-line bg-white px-3 py-2 text-[12px] text-muted">{denied}</div>}
        {resend === 'sent' && <div className="rounded-md border border-line-soft bg-accent-bg px-3 py-2 text-[12px] text-accent-dark" role="status">{t('access.resent')}</div>}
        {resend === 'failed' && <div className="rounded-md border border-[#e8c9c5] bg-danger-bg px-3 py-2 text-[12px] text-danger">{t('access.resendFailed')}</div>}
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => void check()} disabled={checking} className="rounded-[5px] bg-accent py-2 text-center text-[12.5px] font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-send-idle">
            {checking ? t('access.checking') : t('access.checkAgain')}
          </button>
          {reason === 'email_unverified' && onResend && (
            <button
              type="button"
              onClick={() => void resendMail()}
              disabled={resend === 'busy' || resend === 'sent'}
              data-testid="no-access-resend"
              className="rounded-[5px] border border-line-btn bg-white py-2 text-center text-[12.5px] font-medium text-ink transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resend === 'busy' ? t('access.resending') : t('access.resend')}
            </button>
          )}
          {billing ? (
            <button type="button" onClick={() => void openPortal('/billing')} className="rounded-[5px] border border-line-btn bg-white py-2 text-center text-[12.5px] font-medium text-ink transition-colors hover:bg-hover" data-testid="no-access-billing">
              {t('access.openBilling')} ↗
            </button>
          ) : (
            <button type="button" onClick={() => void openPortal('/')} className="rounded-[5px] py-1.5 text-center font-mono text-[11px] text-muted hover:text-ink" data-testid="no-access-portal">
              {t('access.openPortal')}
            </button>
          )}
          <button type="button" onClick={onSignOut} className="rounded-[5px] py-1.5 text-center font-mono text-[11px] text-muted hover:text-ink">
            {t('access.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}
